import { useQueryClient } from '@tanstack/react-query';
import { FetchWorkspaceResponse } from '@zond/api-interfaces';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@zond/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { client } from '../../../client';
import { useStore } from '../../store';
import { useCreateWorkspace } from './workspace.queries';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateWorkspaceModal({ isOpen, onClose }: CreateWorkspaceModalProps) {
  const { t } = useTranslation('workspace');
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const createWorkspace = useCreateWorkspace();
  const queryClient = useQueryClient();

  // Get store actions
  const setWorkspaces = useStore((state) => state.setWorkspaces);
  const getAllWorkspaces = useStore((state) => state.getAllWorkspaces);
  const setCurrentWorkspace = useStore((state) => state.setCurrentWorkspace);

  // Reset field value when opening/closing the modal
  useEffect(() => {
    if (!isOpen) {
      setName('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      // Create the workspace
      const newWorkspace = await createWorkspace.mutateAsync({ name });

      // Fetch the complete workspace data
      const fullWorkspace = await queryClient.fetchQuery<FetchWorkspaceResponse>({
        queryKey: ['workspace', newWorkspace.id],
        queryFn: () => client.get(`/workspaces/${newWorkspace.id}`).then((res) => res.data),
      });

      // Update workspaces list in store
      const currentWorkspaces = getAllWorkspaces();
      setWorkspaces([...currentWorkspaces, newWorkspace]);

      // Set as current workspace
      setCurrentWorkspace(fullWorkspace);

      toast.success(t('create.toasts.created'));
      // Call onClose to close the modal window
      onClose();
      // Navigate to the new workspace
      navigate(`/${newWorkspace.id}`);
    } catch (error) {
      console.error('Failed to create workspace:', error);
      toast.error(t('create.toasts.createFailed'));
    }
  };

  // Handler for modal state changes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('create.nameLabel')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('create.namePlaceholder')}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={!name.trim() || createWorkspace.isPending}>
              {createWorkspace.isPending ? t('create.submitting') : t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
